import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Reveal from "@/components/Reveal";
import { NewsletterBlock } from "@/components/Layout";
import { useContent } from "@/context/ContentContext";
import Seo, { SITE } from "@/components/Seo";

const cardClass = "bg-[#FBF9F4] border border-[#E7DFD0] rounded-3xl shadow-[0_8px_30px_-14px_rgba(28,24,21,0.16)] hover:shadow-[0_20px_46px_-18px_rgba(225,37,27,0.28)] hover:-translate-y-1 transition-all duration-300";

export default function Blog() {
  const { blog } = useContent();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("All");
  const [loading, setLoading] = useState(true);

  const load = useCallback((cat) => {
    setLoading(true);
    const params = cat && cat !== "All" ? { category: cat } : {};
    api.get("/posts", { params }).then((r) => setPosts(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(["All", ...r.data])).catch(() => {});
    load("All");
  }, [load]);

  const pick = (cat) => {
    setActive(cat);
    load(cat);
  };

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <div className="pt-20">
      <Seo
        title="AI Journey | B2B Marketing & AI Insights | Kim Kornachuk"
        description="Field notes on B2B marketing, go-to-market strategy, and AI: demand generation, GEO, positioning, and career lessons from a hands-on growth leader."
        path="/blog"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "AI Journey",
          url: `${SITE}/blog`,
          author: { "@type": "Person", name: "Kim Kornachuk", url: SITE },
        }}
      />
      <section className="relative overflow-hidden">
        <div className="absolute inset-y-0 left-0 w-1/2 orange-wash opacity-50 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-16 sm:pt-24 pb-12 relative">
          <Reveal>
            <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[var(--brand-primary)] mb-6 t-heading">{blog?.kicker || "AI Journey"}</p>
            <h1 className="font-display text-6xl sm:text-8xl leading-[0.92] tracking-tight max-w-4xl whitespace-pre-line t-heading">
              {blog?.heading}
            </h1>
            {blog?.subheading && (
              <p className="mt-6 text-lg text-[var(--brand-muted)] font-medium max-w-2xl t-body">{blog.subheading}</p>
            )}
          </Reveal>

          <div className="mt-12 flex flex-wrap gap-3" data-testid="category-filters">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`category-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => pick(c)}
                className={`px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-[0.1em] transition-all duration-300 border ${
                  active === c
                    ? "bg-[var(--brand-primary)] text-[var(--brand-cream)] border-[var(--brand-primary)] shadow-[0_8px_24px_-10px_rgba(225,37,27,0.6)]"
                    : "bg-[#FBF9F4] text-[var(--brand-heading)] border-[#E7DFD0] hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {loading ? (
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20 text-[var(--brand-muted)] font-bold uppercase tracking-wide">Loading articles…</div>
      ) : posts.length === 0 ? (
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20 text-[var(--brand-muted)] font-bold uppercase tracking-wide">No articles yet. Check back soon.</div>
      ) : (
        <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-20 pt-8">
          {featured && (
            <Reveal>
              <Link to={`/blog/${featured.slug}`} data-testid="featured-post" className={`group grid md:grid-cols-12 items-stretch mb-20 overflow-hidden ${cardClass}`}>
                <div className="md:col-span-7 overflow-hidden">
                  <img src={featured.cover_image} alt={featured.title} fetchPriority="high" decoding="async" className="w-full h-full min-h-[360px] object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="md:col-span-5 p-8 sm:p-12 flex flex-col justify-center">
                  <p className="font-mono-brand text-xs uppercase tracking-[0.18em] text-[var(--brand-primary)]">{featured.category} · {featured.read_minutes} min</p>
                  <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-[1] tracking-tight group-hover:text-[var(--brand-primary)] transition-colors">{featured.title}</h2>
                  <p className="mt-5 text-base text-[var(--brand-muted)] leading-relaxed font-medium">{featured.excerpt}</p>
                  <span className="inline-block mt-6 text-sm font-bold uppercase tracking-[0.12em] text-[var(--brand-primary)]">Read article →</span>
                </div>
              </Link>
            </Reveal>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((p, i) => (
              <Reveal key={p._id} delay={(i % 3) * 0.08}>
                <Link to={`/blog/${p.slug}`} data-testid={`post-card-${i}`} className={`group block overflow-hidden h-full ${cardClass}`}>
                  <div className="overflow-hidden">
                    <img src={p.cover_image} alt={p.title} loading="lazy" decoding="async" className="w-full h-56 object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <p className="font-mono-brand text-xs uppercase tracking-[0.18em] text-[var(--brand-primary)]">{p.category} · {p.read_minutes} min</p>
                    <h3 className="font-display text-2xl mt-3 leading-tight tracking-tight group-hover:text-[var(--brand-primary)] transition-colors">{p.title}</h3>
                    <p className="mt-3 text-[var(--brand-muted)] leading-relaxed line-clamp-3 font-medium">{p.excerpt}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <NewsletterBlock />
    </div>
  );
}
